import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { AlertTriangle, Info, CheckCircle, HelpCircle } from 'lucide-react';
import { useIdioma } from '../i18n/idioma';
import { Button } from '../components/ui/Button';
import AddressAutocomplete from '../components/AddressAutocomplete';

type DialogType = 'danger' | 'info' | 'success' | 'warning';
type DialogMode = 'alert' | 'confirm' | 'prompt';

/** Lo que `validateInput` tiene que decir de lo escrito: qué frena y qué solo
 *  hay que saber. Los dos campos son opcionales; `null` en ambos es «vale». */
export interface DialogInputCheck {
  /** Apaga el guardado y se enseña en rojo. */
  error?: string | null;
  /** Se enseña en ámbar y NO impide guardar. */
  warning?: string | null;
}

interface DialogOptions {
  title: string;
  message: string;
  type?: DialogType;
  confirmText?: string;
  cancelText?: string;
  inputPlaceholder?: string;
  /** Solo en `showPrompt`: qué caja se dibuja.
   *
   *  `'address'` monta el buscador de Google Places, el mismo que el formulario
   *  de cliente. Lo pide el cuadro que reclama la dirección al convertir un
   *  prospecto: era el ÚNICO sitio de la app donde se escribe una dirección a
   *  pelo, sin sugerencias, y las que salían de ahí entraban en las rutas y en
   *  las propuestas mal escritas o sin ciudad. */
  inputKind?: 'text' | 'address';
  /** Solo en `showPrompt`: se llama con lo escrito en cada tecla. Deja decir
   *  «esto no lo puedo guardar» y «esto lo guardo, pero entérate» sin que quien
   *  llama tenga que reabrir el cuadro para contarlo. */
  validateInput?: (value: string) => DialogInputCheck | null;
  /** Solo en `showPrompt`: con qué texto abre la caja.
   *
   *  Existe para poder VOLVER a preguntar sin castigar. Cuando lo escrito no
   *  vale —una dirección que ya ocupa otra ficha, por ejemplo—, quien llama
   *  reabre el cuadro con el motivo en el mensaje y el texto todavía puesto,
   *  para corregir una palabra en vez de teclearlo entero otra vez. */
  defaultValue?: string;
}

interface DialogContextType {
  confirm: (options: DialogOptions) => Promise<boolean>;
  showAlert: (options: Omit<DialogOptions, 'cancelText'>) => Promise<void>;
  showPrompt: (options: DialogOptions) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useConfirm = () => {
  const context = useContext(DialogContext);
  if (!context) throw new Error('useConfirm must be used within a DialogProvider');
  return context;
};

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { t } = useIdioma();
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<DialogOptions>({ title: '', message: '' });
  const [mode, setMode] = useState<DialogMode>('confirm');
  const [inputValue, setInputValue] = useState('');
  /** ¿Ya intentó guardar? El error se calcula desde la primera tecla, pero
   *  enseñarlo desde ahí regaña por escribir el número de la calle antes que la
   *  calle. Se guarda para el momento en que la persona dice que ha terminado. */
  const [intentado, setIntentado] = useState(false);
  const [resolvePromise, setResolvePromise] = useState<((value: any) => void) | null>(null);

  const confirm = useCallback((opts: DialogOptions) => {
    setOptions({
      type: 'danger',
      confirmText: t('dialogo.boton.confirmar'),
      cancelText: t('dialogo.boton.cancelar'),
      ...opts
    });
    setMode('confirm');
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      setResolvePromise(() => resolve);
    });
  }, []);

  const showAlert = useCallback((opts: Omit<DialogOptions, 'cancelText'>) => {
    setOptions({
      type: 'info',
      confirmText: t('dialogo.boton.ok'),
      ...opts
    });
    setMode('alert');
    setIsOpen(true);
    return new Promise<void>((resolve) => {
      setResolvePromise(() => resolve);
    });
  }, []);

  const showPrompt = useCallback((opts: DialogOptions) => {
    setOptions({
      type: 'info',
      confirmText: t('dialogo.boton.enviar'),
      cancelText: t('dialogo.boton.cancelar'),
      inputPlaceholder: t('dialogo.input.placeholder'),
      ...opts
    });
    setMode('prompt');
    setInputValue(opts.defaultValue ?? '');
    setIntentado(false);
    setIsOpen(true);
    return new Promise<string | null>((resolve) => {
      setResolvePromise(() => resolve);
    });
  }, []);

  const handleClose = (result: boolean) => {
    setIsOpen(false);
    if (resolvePromise) {
      if (mode === 'prompt') {
        resolvePromise(result ? inputValue : null);
      } else if (mode === 'alert') {
        resolvePromise(undefined);
      } else {
        resolvePromise(result);
      }
      setResolvePromise(null);
    }
  };

  /* Se recalcula en cada render y no se guarda en estado: es una función pura de
     lo escrito, y un `useEffect` que copiara esto a un estado enseñaría durante
     un fotograma el veredicto de la tecla anterior. */
  const check = mode === 'prompt' && options.validateInput
    ? options.validateInput(inputValue)
    : null;
  const inputError = check?.error ?? null;
  const inputWarning = check?.warning ?? null;

  /* Guardar CON un error no cierra el cuadro: lo enseña.
     La alternativa —apagar el botón— deja a alguien pulsando algo que no
     responde y sin nada escrito que explique por qué. */
  const confirmar = () => {
    if (inputError) {
      setIntentado(true);
      return;
    }
    handleClose(true);
  };

  // --- ICON HELPER ---
  // Cuadro chico (40px, r-lg) con el par semántico fondo/tinta del sistema.
  const ICON_BOX = 'w-10 h-10 shrink-0 rounded-xl grid place-items-center mx-auto sm:mx-0';

  const getIcon = () => {
    switch (options.type) {
      case 'danger':
        return <div className={`${ICON_BOX} bg-peligro-bg text-peligro`}><AlertTriangle size={20} /></div>;
      case 'success':
        return <div className={`${ICON_BOX} bg-exito-bg text-exito`}><CheckCircle size={20} /></div>;
      case 'info':
        return <div className={`${ICON_BOX} bg-navy-50 text-navy-700`}><Info size={20} /></div>;
      default:
        return <div className={`${ICON_BOX} bg-aviso-bg text-aviso`}><HelpCircle size={20} /></div>;
    }
  };

  // Un solo botón de acción: destructivo en peligro, el resto amarillo de marca.
  const confirmVariant = options.type === 'danger' ? 'danger' : 'primary';

  return (
    <DialogContext.Provider value={{ confirm, showAlert, showPrompt }}>
      {children}

      {/* z-[900]: este diálogo es global — lo abre cualquier pantalla, incluidas
          las que ya están encima de todo. A z-[100] quedaba DEBAJO del cajón de
          detalle del estimado (z-[220]), del selector de zona (z-[300]) y de
          varios paneles más: el usuario veía la pregunta a medias y sin poder
          pulsar. Va por encima de todo salvo el splash de carga (z-[9999]), que
          solo aparece antes de que exista nada con lo que confirmar. */}
      {isOpen && (
        <div className="relative z-[900]" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-navy-950/50 animate-in fade-in duration-200"></div>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              {/* Modal Panel */}
              <div className="relative w-full overflow-hidden rounded-3xl bg-white text-left shadow-s3 sm:my-8 sm:max-w-lg animate-in zoom-in-95 fade-in duration-200">
                <div className="px-5 py-5 sm:px-6 sm:py-6">
                  <div className="sm:flex sm:items-start sm:gap-4">
                    {getIcon()}
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full min-w-0">
                      <h3 className="text-base font-semibold tracking-tight leading-6 text-navy-950" id="modal-title">
                        {options.title}
                      </h3>
                      <div className="mt-1.5 text-sm text-acero-500 whitespace-pre-line leading-relaxed">
                        <p>{options.message}</p>
                        {mode === 'prompt' && (options.inputKind === 'address' ? (
                          /* El desplegable de sugerencias sale por un portal a
                             <body> con z-index propio, así que el `overflow`
                             de este panel no lo recorta. */
                          <div className="mt-4">
                            <AddressAutocomplete
                              value={inputValue}
                              onChange={setInputValue}
                              onPlaceSelect={setInputValue}
                              placeholder={options.inputPlaceholder}
                              autoFocus
                              onEnter={confirmar}
                              className="w-full h-10 pl-9 pr-4 bg-white text-navy-950 border border-acero-300 rounded-xl text-sm placeholder:text-acero-400 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                            />
                          </div>
                        ) : (
                          <input
                            type="text"
                            autoFocus
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={options.inputPlaceholder}
                            className="mt-4 w-full h-10 px-4 bg-white text-navy-950 border border-acero-300 rounded-xl text-sm placeholder:text-acero-400 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') confirmar();
                            }}
                          />
                        ))}
                        {mode === 'prompt' && intentado && inputError && (
                          <p className="mt-2 text-xs text-peligro bg-peligro-bg border border-peligro/25 px-2.5 py-1.5 rounded-lg">
                            {inputError}
                          </p>
                        )}
                        {/* El aviso calla mientras haya un error: dos mensajes a
                            la vez sobre el mismo campo, uno de los cuales frena y
                            el otro no, no se leen — se leen a medias. */}
                        {mode === 'prompt' && !inputError && inputWarning && (
                          <p className="mt-2 text-xs text-aviso bg-aviso-bg border border-aviso/25 px-2.5 py-1.5 rounded-lg">
                            {inputWarning}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-acero-50 border-t border-acero-200 px-5 py-4 sm:px-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  {mode !== 'alert' && (
                    <Button
                      variant="ghost"
                      className="w-full sm:w-auto"
                      onClick={() => handleClose(false)}
                    >
                      {options.cancelText}
                    </Button>
                  )}
                  <Button
                    variant={confirmVariant}
                    className="w-full sm:w-auto"
                    onClick={confirmar}
                  >
                    {options.confirmText}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};
